// src/pages/Dashboard.js
import { useEffect, useState } from "react";
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  query,
  where,
  getCountFromServer,
  getDocs,
} from "firebase/firestore";

/* ---------- env helper (CRA) ---------- */
function env(key, fallback = "") {
  return process.env[`REACT_APP_${key}`] ?? fallback;
}

/* ---------- Firebase config ---------- */
const firebaseConfig = {
  apiKey     : env("FIREBASE_API_KEY"),
  authDomain : env("FIREBASE_AUTH_DOMAIN"),
  projectId  : env("FIREBASE_PROJECT_ID"),
  appId      : env("FIREBASE_APP_ID"),
};

const ORDERS_COLLECTION =
  env("BUILD_TYPE", "staging") === "staging"
    ? "orders-staging"
    : "orders-production";

const EXTERNAL_CODE_POOL_COLLECTION =
  env("BUILD_TYPE", "staging") === "staging"
    ? "external-code-pool-staging"
    : "external-code-pool-production";    

/* ---------- init Firebase ---------- */
const app = initializeApp(firebaseConfig);
const db  = getFirestore(app);

/* ---------- helper to fetch order stats ---------- */
async function fetchCounts() {
  const col = collection(db, ORDERS_COLLECTION);

  const [tot, proc, procd, email, comp, err] = await Promise.all([
    getCountFromServer(query(col)),
    getCountFromServer(query(col, where("orderStatus", "==", "processing"))),
    getCountFromServer(query(col, where("orderStatus", "==", "processed"))),
    getCountFromServer(query(col, where("orderStatus", "==", "emailing"))),
    getCountFromServer(query(col, where("orderStatus", "==", "complete"))),
    getCountFromServer(query(col, where("orderStatus", "==", "error"))),
  ]);

  return {
    total      : tot.data().count,
    processing : proc.data().count,
    processed  : procd.data().count,
    emailing   : email.data().count,
    complete   : comp.data().count,
    error      : err.data().count,
  };
}

/* ---------- helper to fetch inventory info ---------- */
async function fetchInventory() {
  const productsCol = collection(db, "products");

  // Step 1: Get all products that require external codes
  const externalProductsQuery = query(productsCol, where("activationCodeSource", "==", "external"));
  const externalProductsSnap = await getDocs(externalProductsQuery);

  const productList = externalProductsSnap.docs.map(doc => {
    const data = doc.data();
    return {
      name: data.name,
      productCode: data.productCode,
    };
  });

  // Step 2: For each product, count unreserved codes
  const codeCounts = await Promise.all(
    productList.map(async (product) => {
      const codePoolQuery = query(
        collection(db, EXTERNAL_CODE_POOL_COLLECTION),
        where("productCode", "==", product.productCode),
        where("reserved", "==", false)
      );

      const countSnap = await getCountFromServer(codePoolQuery);

      return {
        name: product.name,
        productCode: product.productCode,
        availableCodes: countSnap.data().count,
      };
    })
  );

  return codeCounts;
}

/* ---------- Dashboard component ---------- */
export default function Dashboard() {
  const [stats, setStats]       = useState({ total: 0, processing: 0, processed:0, emailing: 0, complete: 0, error: 0 });
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [fault, setFault]       = useState(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const [orderStats, inventoryData] = await Promise.all([
          fetchCounts(),
          fetchInventory(),
        ]);
        if (mounted) {
          setStats(orderStats);
          setInventory(inventoryData);
          setFault(null);
        }
      } catch (e) {
        if (mounted) setFault(e.message || String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load(); // first run
    const id = setInterval(load, 60_000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  if (loading) return <p>Loading dashboard…</p>;
  if (fault)   return <p style={{ color: "red" }}>⚠ {fault}</p>;

  return (
    <div style={{ maxWidth: 420, lineHeight: 1.6 }}>
      {/* Orders Section */}
      <h2>Orders&nbsp;(<code>{ORDERS_COLLECTION}</code>)</h2>
      {!stats.total ? (
        <p>No orders found.</p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0 }}>
          <li><b>Total&nbsp;&nbsp;&nbsp;:</b> {stats.total}</li>
          <br />
          <li><b>Processing:</b> {stats.processing}</li>
          <li><b>Processed:</b> {stats.processed}</li>
          <li><b>Emailing:</b> {stats.emailing}</li>
          <br />
          <li><b>Complete:</b> {stats.complete}</li>
          <li><b>Error&nbsp;&nbsp;:</b> {stats.error}</li>
        </ul>
      )}

      {/* Inventory Section */}
      <h2 style={{ marginTop: "2em" }}>Inventory&nbsp;(<code>{ORDERS_COLLECTION}</code>)</h2>
        {inventory.length === 0 ? (
        <p>No external-code products found.</p>
        ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "0.5em" }}>
            <thead>
            <tr>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ccc", paddingBottom: "0.25em" }}>Product Name</th>
                <th style={{ textAlign: "left", borderBottom: "1px solid #ccc", paddingBottom: "0.25em" }}>Product Code</th>
                <th style={{ textAlign: "right", borderBottom: "1px solid #ccc", paddingBottom: "0.25em" }}>Available Codes</th>
            </tr>
            </thead>
            <tbody>
            {inventory.map(item => (
                <tr key={item.productCode}>
                <td style={{ padding: "0.25em 0" }}>{item.name}</td>
                <td style={{ padding: "0.25em 0" }}>{item.productCode}</td>
                <td
                    style={{
                    padding: "0.25em 0",
                    textAlign: "right",
                    color: item.availableCodes <= 50 ? "red" : "inherit",
                    fontWeight: item.availableCodes <= 50 ? "bold" : "normal",
                    }}
                >
                    {item.availableCodes}
                </td>
                </tr>
            ))}
            </tbody>
        </table>
        )}
    </div>
  );
}
